/* eslint-disable react/prop-types */
/**
 * LearnedIbansChips — widget pokazujący IBAN-y „nauczone" przez Agenta Wyciągów.
 *
 * Wejście:
 *  - ibans: array<string>             — `profile.ibans`
 *  - currentIban: string | null       — `profile.counterparty_iban` (dominujący)
 *  - history: array<object>           — `profile.iban_history`
 *  - learningStatus: string           — `profile.iban_learning_status`
 *  - testIdPrefix: string             — prefix dla data-testid
 *
 * Render:
 *  - jeśli `ibans` jest puste → null (nic nie pokazujemy),
 *  - inaczej: poziomy rząd chip-ów (kolory wg `learningStatus`),
 *  - klik w chip → Popover z historią z `iban_history` (transactions_count,
 *    okres, suma, is_current).
 *
 * Anti-fraud sygnał: gdy `learningStatus === "new_iban_detected"` lub
 * `multiple_ibans`, chip jest pomarańczowy/czerwony — od razu rzuca się w oczy
 * w liście kontrahentów.
 *
 * NIE wprowadza żadnej logiki biznesowej — jest czystym readonly viewerem
 * danych zwróconych przez Agenta Wyciągów.
 */
import { useState } from "react";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { Wallet, AlertTriangle, ShieldCheck } from "lucide-react";

const formatPLN = (v) => {
  const n = Number(v || 0);
  return `${n.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} PLN`;
};

// Maskowanie środka IBAN dla czytelności (zachowuje 4 początek + 4 koniec).
const maskIban = (iban) => {
  const s = String(iban || "");
  if (s.length <= 10) return s;
  return `${s.slice(0, 4)}…${s.slice(-4)}`;
};

/** Kolor + ikona chip-a wg statusu uczenia. */
const chipClass = (isCurrent, learningStatus) => {
  if (learningStatus === "new_iban_detected") {
    return "bg-rose-50 text-rose-800 border-rose-300 hover:bg-rose-100";
  }
  if (learningStatus === "multiple_ibans") {
    return isCurrent
      ? "bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100"
      : "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200";
  }
  if (learningStatus === "stable") {
    return isCurrent
      ? "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100"
      : "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200";
  }
  return "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200";
};

const StatusLegend = ({ learningStatus, testIdPrefix }) => {
  if (!learningStatus || learningStatus === "no_iban") return null;
  const isAlert = learningStatus === "new_iban_detected" || learningStatus === "multiple_ibans";
  const Icon = isAlert ? AlertTriangle : ShieldCheck;
  const cls = isAlert
    ? "text-amber-700"
    : (learningStatus === "stable" ? "text-emerald-700" : "text-gray-600");
  const labelMap = {
    stable: "stabilny IBAN",
    new_iban_detected: "nowy IBAN — zweryfikuj",
    multiple_ibans: "kilka IBAN-ów",
    unknown: "status nieznany",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] ${cls}`}
      data-testid={`${testIdPrefix}-status`}
      title={`iban_learning_status = ${learningStatus}`}
    >
      <Icon className="w-3 h-3" />
      {labelMap[learningStatus] || learningStatus}
    </span>
  );
};

const IbanHistoryCard = ({ iban, hist }) => {
  if (!hist) {
    return (
      <div className="text-[11px] text-gray-500">
        Brak historii dla tego IBAN-u w profilu (LLM nie zwrócił szczegółów).
      </div>
    );
  }
  return (
    <div className="space-y-1.5 text-[11px] text-gray-700">
      <div className="flex items-center gap-1.5">
        <Wallet className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
        <span className="font-mono break-all text-gray-900">{iban}</span>
        {hist.is_current ? (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-700">
            aktualny
          </span>
        ) : null}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
        <div>
          <div className="text-[10px] text-gray-500">Transakcje</div>
          <div className="font-medium tabular-nums">{hist.transactions_count ?? "—"}</div>
        </div>
        <div>
          <div className="text-[10px] text-gray-500">Suma</div>
          <div className={`font-medium tabular-nums ${Number(hist.total_amount) < 0 ? "text-red-700" : "text-gray-900"}`}>
            {formatPLN(hist.total_amount)}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-gray-500">Pierwsza</div>
          <div className="tabular-nums">{hist.first_seen_at || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] text-gray-500">Ostatnia</div>
          <div className="tabular-nums">{hist.last_seen_at || "—"}</div>
        </div>
      </div>
    </div>
  );
};

const LearnedIbansChips = ({
  ibans, currentIban, history, learningStatus, testIdPrefix = "learned-ibans",
}) => {
  const list = Array.isArray(ibans) ? ibans.filter(Boolean) : [];
  const [openIban, setOpenIban] = useState(null);

  if (!list.length) return null;

  // Mapa IBAN → wpis z iban_history (po pierwszym znalezionym).
  const histByIban = new Map();
  (Array.isArray(history) ? history : []).forEach((h) => {
    if (h && h.iban && !histByIban.has(h.iban)) histByIban.set(h.iban, h);
  });

  return (
    <div
      className="mt-1.5 flex flex-wrap items-center gap-1.5"
      data-testid={`${testIdPrefix}-row`}
    >
      <span className="text-[10px] text-gray-500 uppercase tracking-wide">
        IBAN-y nauczone:
      </span>
      {list.map((iban) => {
        const isCurrent = iban === currentIban;
        const cls = chipClass(isCurrent, learningStatus);
        const hist = histByIban.get(iban);
        return (
          <Popover
            key={iban}
            open={openIban === iban}
            onOpenChange={(o) => setOpenIban(o ? iban : null)}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono border transition-colors ${cls}`}
                data-testid={`${testIdPrefix}-chip-${iban}`}
                title={iban}
              >
                <Wallet className="w-3 h-3" />
                {maskIban(iban)}
                {isCurrent ? <span className="text-[9px] not-italic">·akt.</span> : null}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-80 max-w-[92vw] p-3"
              data-testid={`${testIdPrefix}-popover-${iban}`}
            >
              <div className="text-[11px] text-gray-500 mb-1.5">
                Historia IBAN-u w transakcjach kontrahenta
              </div>
              <IbanHistoryCard iban={iban} hist={hist} />
            </PopoverContent>
          </Popover>
        );
      })}
      <StatusLegend learningStatus={learningStatus} testIdPrefix={testIdPrefix} />
    </div>
  );
};

export default LearnedIbansChips;
